/**
 * Text primitives for turning raw field keys into display labels and
 * for ignore-key matching. Separator-agnostic: "created_by",
 * "Created By", and "createdBy" all normalize to "createdby".
 */

export const normalizeKey = (key: string): string =>
  key.toLowerCase().replace(/[\s_-]+/g, "");

/**
 * True when the key matches an ignored key exactly (after
 * normalization). For matching labels that carry prefixes
 * ("Branch - PIC Email"), use containsIgnoredKey.
 */
export const isIgnoredKey = (
  key: string,
  ignored: ReadonlySet<string> | ReadonlyArray<string>,
): boolean => {
  const normalized = normalizeKey(key);
  return toArray(ignored).some((ik) => normalizeKey(ik) === normalized);
};

/**
 * True when the label contains any ignored key as a fragment. Weaker
 * than isIgnoredKey — use for prefixed display labels only.
 */
export const containsIgnoredKey = (
  label: string,
  ignored: ReadonlySet<string> | ReadonlyArray<string>,
): boolean => {
  const normalized = normalizeKey(label);
  return toArray(ignored).some((ik) => normalized.includes(normalizeKey(ik)));
};

/** snake_case / camelCase → "Title Case". */
export const titleCase = (key: string): string =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Any-case field key → snake_case: "CustomerName", "customer_name",
 * and "KTPNumber" all become "ktp_number". Backend field keys arrive
 * in mixed dialects; translations are keyed snake_case.
 */
export const toSnakeCase = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .toLowerCase();

/**
 * Look up a display label for a key: exact, then case-insensitive,
 * then title-case fallback. `labels` is domain data.
 */
export const labelFor = (
  labels: Record<string, string>,
  key: string,
): string => {
  if (labels[key]) return labels[key];
  if (labels[key.toLowerCase()]) return labels[key.toLowerCase()];
  return titleCase(key);
};

const toArray = (
  ignored: ReadonlySet<string> | ReadonlyArray<string>,
): readonly string[] =>
  Array.isArray(ignored) ? ignored : Array.from(ignored as Set<string>);
